import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  BarChart3,
  Check,
  CreditCard,
  FlaskConical,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquare,
  Palette,
  PlugZap,
  RefreshCw,
  Settings,
  Shield,
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { ACCENT_OPTIONS, BACKGROUND_OPTIONS, useTheme } from "../context/ThemeContext";
import useMediaQuery from "../hooks/useMediaQuery";
import { cn } from "../lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";

const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, href: "/dashboard", match: ({ pathname }) => pathname === "/dashboard" },
  { id: "statistics", label: "Statistics", icon: BarChart3, href: "/statistics", match: ({ pathname }) => pathname.startsWith("/statistics") },
  { id: "broker", label: "Broker Connect", icon: PlugZap, href: "/onboarding/broker-connect", match: ({ pathname }) => pathname.startsWith("/onboarding/") },
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    href: "/settings?tab=accounts",
    match: ({ pathname, search }) => pathname.startsWith("/settings") && new URLSearchParams(search).get("tab") !== "backtest",
  },
  {
    id: "backtest",
    label: "Backtest",
    icon: FlaskConical,
    href: "/settings?tab=backtest",
    match: ({ pathname, search }) => pathname.startsWith("/backtest") || (pathname.startsWith("/settings") && new URLSearchParams(search).get("tab") === "backtest"),
  },
  { id: "subscription", label: "Subscription", icon: CreditCard, href: "/subscription", match: ({ pathname }) => pathname.startsWith("/subscription") },
  { id: "admin", label: "Admin Dashboard", icon: Shield, href: "/admin", adminOnly: true, match: ({ pathname }) => pathname.startsWith("/admin") },
];
const PRIMARY_MOBILE_NAV_IDS = ["dashboard", "broker", "settings", "backtest"];

const THEME_OPTIONS = [
  {
    id: "system",
    label: "System",
    description: "Follow OS appearance",
    swatch: "linear-gradient(135deg, #f0f0f0 50%, #0a0a0a 50%)",
    contrast: "Adaptive",
    note: "Best when you want the app to match the device.",
    preview: {
      background: "linear-gradient(135deg, #f5f5f5 0%, #f5f5f5 50%, #09090b 50%, #09090b 100%)",
      panel: "rgba(255,255,255,0.8)",
      text: "#111827",
      muted: "#4b5563",
      accent: "#3b82f6",
      accentSoft: "rgba(59,130,246,0.16)",
      border: "rgba(255,255,255,0.18)",
    },
  },
  {
    id: "light",
    label: "Daylight",
    description: "Clean white panels with stronger reading contrast",
    swatch: "linear-gradient(135deg, #ffffff, #dbeafe)",
    contrast: "High",
    note: "Best when the room is bright or you want maximum legibility.",
    preview: {
      background: "linear-gradient(145deg, #ffffff, #edf2f7)",
      panel: "rgba(255,255,255,0.92)",
      text: "#111827",
      muted: "#4b5563",
      accent: "#2563eb",
      accentSoft: "rgba(37,99,235,0.14)",
      border: "rgba(15,23,42,0.08)",
    },
  },
  {
    id: "dark",
    label: "Carbon",
    description: "Balanced dark workspace with clear text hierarchy",
    swatch: "linear-gradient(135deg, #0c0f14, #1f2937)",
    contrast: "High",
    note: "Recommended default for dense trading and settings screens.",
    preview: {
      background: "linear-gradient(145deg, #0c0f14, #171b22)",
      panel: "rgba(24,30,40,0.94)",
      text: "#f8fafc",
      muted: "#d1d9e6",
      accent: "#7dadff",
      accentSoft: "rgba(79,140,255,0.22)",
      border: "rgba(148,163,184,0.18)",
    },
  },
  {
    id: "midnight",
    label: "Harbor",
    description: "Blue-black focus mode with brighter secondary text",
    swatch: "linear-gradient(135deg, #0a1628, #12395b)",
    contrast: "High",
    note: "A calmer dark theme for charts without losing readability.",
    preview: {
      background: "radial-gradient(circle at top left, #173f64 0%, #0a1628 52%, #07101d 100%)",
      panel: "rgba(16,33,56,0.94)",
      text: "#f8fbff",
      muted: "#d5e4f8",
      accent: "#38bdf8",
      accentSoft: "rgba(56,189,248,0.22)",
      border: "rgba(148,163,184,0.2)",
    },
  },
  {
    id: "glass",
    label: "Prism",
    description: "Frosted panels with improved contrast",
    swatch: "linear-gradient(135deg, rgba(79,140,255,0.42), rgba(155,124,255,0.36)), #111827",
    contrast: "Medium",
    note: "More visual, now tuned to keep settings text readable.",
    preview: {
      background: "linear-gradient(145deg, rgba(37,99,235,0.28), rgba(124,58,237,0.2)), #111827",
      panel: "rgba(255,255,255,0.12)",
      text: "#f8fafc",
      muted: "#e2e8f0",
      accent: "#c084fc",
      accentSoft: "rgba(192,132,252,0.22)",
      border: "rgba(255,255,255,0.22)",
    },
  },
];

function RailButton({ label, icon: Icon, active = false, onClick, className }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={label}
          title={label}
          onClick={onClick}
          className={cn(
            "glass-unfilled-control relative h-11 w-11 rounded-xl border border-white/18 bg-white/[0.08] text-zinc-100 transition-all hover:border-white/30 hover:bg-white/[0.14] hover:text-white",
            active && "scale-[1.03] border-blue-300/90 bg-blue-500/32 text-white shadow-[0_0_26px_rgba(59,130,246,0.34)] before:absolute before:-left-2 before:top-1/2 before:h-7 before:w-1 before:-translate-y-1/2 before:rounded-full before:bg-blue-300 before:shadow-[0_0_14px_rgba(147,197,253,0.8)]",
            className,
          )}
        >
          <Icon className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right" className="border-white/10 bg-black text-white">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export default function PlatformShell({
  title,
  titleIcon: TitleIcon = BarChart3,
  backTo = "/dashboard",
  onBack,
  children,
  headerActions = null,
  currentKey,
  extraRailActions = [],
  mainClassName,
  showUserEmail = true,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const { theme, setTheme, accentColor, setAccentColor, backgroundTone, setBackgroundTone } = useTheme();
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const activeKey = useMemo(() => {
    if (currentKey) return currentKey;
    const match = NAV_ITEMS.find((item) => item.match(location));
    return match?.id || "dashboard";
  }, [currentKey, location]);

  const visibleNavItems = NAV_ITEMS.filter((item) => !item.adminOnly || user?.role === "admin");
  const mobilePrimaryNavItems = visibleNavItems.filter((item) => PRIMARY_MOBILE_NAV_IDS.includes(item.id));
  const mobileMoreActive = !mobilePrimaryNavItems.some((item) => item.id === activeKey);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(backTo);
  };

  return (
    <TooltipProvider>
      <div
        className="min-h-screen overflow-x-hidden text-white lg:h-screen lg:overflow-hidden"
        style={{
          backgroundColor: "var(--app-shell-bg)",
          backgroundImage: "var(--app-shell-gradient)",
          backgroundAttachment: "fixed",
          backgroundSize: "cover",
        }}
      >
        <aside
          className="fixed inset-y-0 left-0 z-40 hidden w-[88px] flex-col border-r border-white/10 px-3 py-4 backdrop-blur-xl lg:flex"
          style={{ background: "var(--app-shell-overlay)" }}
        >
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mb-6 flex flex-col items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-2 py-3 text-center transition hover:bg-white/[0.06]"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/15 text-blue-400">
              <Activity className="h-5 w-5" />
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-zinc-300">
              TradoPro
            </div>
          </button>

          <div className="flex flex-1 flex-col items-center gap-3">
            {visibleNavItems.map((item) => (
              <RailButton
                key={item.id}
                label={item.label}
                icon={item.icon}
                active={activeKey === item.id}
                onClick={() => navigate(item.href)}
              />
            ))}

            {extraRailActions.length > 0 ? (
              <>
                <div className="my-2 h-px w-10 bg-white/10" />
                {extraRailActions.map((action) => (
                  <RailButton
                    key={action.id}
                    label={action.label}
                    icon={action.icon || RefreshCw}
                    onClick={action.onClick}
                    className={action.className}
                  />
                ))}
              </>
            ) : null}
          </div>

          <div className="mt-4 flex flex-col items-center gap-3">
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="glass-unfilled-control h-11 w-11 rounded-xl border border-white/18 bg-white/[0.08] text-zinc-100 transition-all hover:border-white/30 hover:bg-white/[0.14] hover:text-white"
                    >
                      <Palette className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="right" className="border-white/10 bg-black text-white">
                  {`Appearance: ${THEME_OPTIONS.find((item) => item.id === theme)?.label || "Theme"}`}
                </TooltipContent>
              </Tooltip>
              <DropdownMenuContent
                side="right"
                align="end"
                className="w-[320px] border-white/15 bg-[#10141b] p-3 text-white shadow-2xl"
              >
                <DropdownMenuLabel className="px-1 text-xs uppercase tracking-[0.18em] text-zinc-300">
                  Appearance
                </DropdownMenuLabel>
                <div className="px-1 pb-2 pt-1 text-xs leading-5 text-zinc-300">
                  Choose the preset, then tune the backdrop and accent color used across the workspace.
                </div>
                <DropdownMenuSeparator className="mb-2 bg-white/10" />
                {THEME_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.id}
                    onClick={() => setTheme(option.id, token)}
                    className={cn(
                      "mb-1.5 rounded-lg border border-white/12 px-2 py-2 text-zinc-100 focus:bg-white/10 focus:text-white",
                      theme === option.id && "border-[var(--ui-accent-border)] bg-[var(--ui-accent-soft)] text-[var(--ui-accent-text)]",
                    )}
                  >
                    <div className="flex w-full items-center gap-3">
                      <span
                        className="h-6 w-6 rounded-md border border-white/10"
                        style={{ background: option.swatch }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium">{option.label}</div>
                        <div className="truncate text-xs text-zinc-300">{option.description}</div>
                      </div>
                      <span className="rounded-full border border-white/15 bg-white/5 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.16em] text-zinc-200">
                        {option.contrast}
                      </span>
                      {theme === option.id ? (
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[var(--ui-accent-soft)] text-[var(--ui-accent-text)]">
                          <Check className="h-3.5 w-3.5" />
                        </span>
                      ) : null}
                    </div>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator className="my-2 bg-white/10" />
                <div className="px-1 pb-2 text-[11px] uppercase tracking-[0.18em] text-zinc-300">
                  Background
                </div>
                <div className="grid grid-cols-5 gap-2 px-1">
                  {BACKGROUND_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setBackgroundTone(option.id, token)}
                      className={cn(
                        "flex h-9 items-center justify-center rounded-lg border transition",
                        backgroundTone === option.id ? "border-white/40 bg-white/10" : "border-white/10 bg-black/20 hover:border-white/20",
                      )}
                      title={option.label}
                    >
                      <span
                        className="h-4 w-4 rounded-full border border-white/10"
                        style={{
                          background: option.swatch,
                          boxShadow: backgroundTone === option.id ? "0 0 0 4px rgba(255,255,255,0.08)" : "none",
                        }}
                      />
                    </button>
                  ))}
                </div>
                <div className="px-1 pt-2 text-xs leading-5 text-zinc-300">
                  Background changes the app shell color while leaving your selected theme and control styles intact.
                </div>
                <DropdownMenuSeparator className="my-2 bg-white/10" />
                <div className="px-1 pb-2 text-[11px] uppercase tracking-[0.18em] text-zinc-300">
                  Accent Color
                </div>
                <div className="grid grid-cols-5 gap-2 px-1">
                  {ACCENT_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setAccentColor(option.id)}
                      className={cn(
                        "flex h-9 items-center justify-center rounded-lg border transition",
                        accentColor === option.id ? "border-white/40 bg-white/10" : "border-white/10 bg-black/20 hover:border-white/20",
                      )}
                      title={option.label}
                    >
                      <span
                        className="h-4 w-4 rounded-full"
                        style={{
                          background: option.color,
                          boxShadow: accentColor === option.id ? `0 0 0 4px ${option.soft}` : "none",
                        }}
                      />
                    </button>
                  ))}
                </div>
                <div className="px-1 pt-2 text-xs leading-5 text-zinc-300">
                  The accent color is used for active tabs like <span className="text-zinc-300">AI</span>, <span className="text-zinc-300">News</span>, and other selected highlights.
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <RailButton
              label="Sign Out"
              icon={LogOut}
              onClick={() => setLogoutConfirmOpen(true)}
              className="text-red-300 hover:border-red-500/30 hover:bg-red-500/10 hover:text-red-200"
            />
          </div>
        </aside>

        <div className="flex min-h-screen flex-col lg:h-screen lg:overflow-hidden lg:pl-[88px]">
          <header className="z-30 border-b border-white/10 backdrop-blur-xl" style={{ background: "var(--app-shell-overlay)" }}>
            <div className="flex min-h-[64px] items-center justify-between gap-4 px-4 sm:px-6">
              <div className="flex min-w-0 items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  aria-label="Back"
                  className="glass-unfilled-control h-10 w-10 shrink-0 border border-white/16 bg-white/[0.06] px-0 text-zinc-100 hover:border-white/28 hover:bg-white/[0.12] hover:text-white sm:w-auto sm:px-3"
                >
                  <ArrowLeft className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Back</span>
                </Button>
                <div className="hidden h-8 w-px bg-white/10 sm:block" />
                <div className="flex min-w-0 items-center gap-2">
                  <TitleIcon className="h-4 w-4 text-zinc-400" />
                  <h1 className="truncate text-sm font-medium text-zinc-300">{title}</h1>
                </div>
              </div>

              <div className="hidden items-center gap-3 md:flex">
                {headerActions}
                {showUserEmail ? (
                  <span className="hidden text-sm text-zinc-500 md:inline">{user?.email}</span>
                ) : null}
              </div>

              <div className="flex items-center gap-2 md:hidden">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Open workspace menu"
                  onClick={() => setMobileMenuOpen(true)}
                  className="glass-unfilled-control h-10 w-10 rounded-xl border border-white/18 bg-white/[0.08] text-zinc-100 hover:border-white/28 hover:bg-white/[0.14] hover:text-white"
                >
                  <Menu className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {headerActions ? (
              <div className="border-t border-white/10 px-4 py-3 md:hidden">
                <div className="flex items-center gap-2 overflow-x-auto pb-1">
                  {headerActions}
                </div>
              </div>
            ) : null}
          </header>

          <main
            className={cn(
              "flex-1 min-h-0 overflow-visible lg:overflow-y-auto",
              isMobile && "pb-[calc(82px+env(safe-area-inset-bottom))]",
              mainClassName,
            )}
          >
            {children}
          </main>
        </div>

        {isMobile ? (
          <nav
            className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur-2xl"
            style={{ background: "var(--app-shell-overlay)" }}
            aria-label="Mobile navigation"
          >
            <div className="mx-auto grid max-w-xl grid-cols-5 gap-2">
              {mobilePrimaryNavItems.map((item) => {
                const Icon = item.icon;
                const active = activeKey === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    aria-label={item.label}
                    aria-current={active ? "page" : undefined}
                    onClick={() => navigate(item.href)}
                    className={cn(
                      "glass-unfilled-control flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-xl border px-1 py-2 transition",
                      active
                        ? "border-[var(--ui-accent-border)] bg-[var(--ui-accent-soft)] text-white shadow-[0_0_22px_var(--ui-accent-soft)]"
                        : "border-white/16 bg-white/[0.08] text-zinc-100 hover:border-white/28 hover:bg-white/[0.14] hover:text-white",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-[9px] font-medium uppercase tracking-[0.1em]">
                      {item.id === "backtest" ? "Test" : item.id === "broker" ? "Broker" : item.label}
                    </span>
                  </button>
                );
              })}
              <button
                type="button"
                aria-label="Open more navigation and appearance options"
                onClick={() => setMobileMenuOpen(true)}
                className={cn(
                  "glass-unfilled-control flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-xl border px-1 py-2 transition",
                  mobileMoreActive
                    ? "border-[var(--ui-accent-border)] bg-[var(--ui-accent-soft)] text-white shadow-[0_0_22px_var(--ui-accent-soft)]"
                    : "border-white/16 bg-white/[0.08] text-zinc-100 hover:border-white/28 hover:bg-white/[0.14] hover:text-white",
                )}
              >
                <Menu className="h-4 w-4" />
                <span className="text-[9px] font-medium uppercase tracking-[0.1em]">More</span>
              </button>
            </div>
          </nav>
        ) : null}

        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent
            side="right"
            className="w-[88vw] max-w-sm overflow-y-auto border-white/10 px-4 py-6 text-white sm:max-w-sm"
            style={{ backgroundColor: "var(--app-shell-sheet)" }}
          >
            <SheetHeader className="mb-6 text-left">
              <SheetTitle className="text-white">Mobile Workspace</SheetTitle>
              <SheetDescription className="text-zinc-400">
                Move through the platform, refresh data, and tune the look without leaving the current screen.
              </SheetDescription>
            </SheetHeader>

            <div className="space-y-6">
              <div className="space-y-2">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-500">
                  Navigation
                </div>
                <div className="grid gap-2">
                  {visibleNavItems.map((item) => {
                    const Icon = item.icon;
                    const active = item.id === activeKey;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          navigate(item.href);
                          setMobileMenuOpen(false);
                        }}
                        className={cn(
                          "glass-unfilled-control flex items-center gap-3 rounded-2xl border px-3 py-3 text-left transition",
                          active
                            ? "border-[var(--ui-accent-border)] bg-[var(--ui-accent-soft)] text-white"
                            : "border-white/16 bg-white/[0.08] text-zinc-100 hover:border-white/28 hover:bg-white/[0.14] hover:text-white",
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="text-sm font-medium">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-500">
                    Quick Actions
                  </div>
                  <div className="grid gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent("tradopro:assistant:open"));
                        setMobileMenuOpen(false);
                      }}
                      className="glass-unfilled-control flex items-center gap-3 rounded-2xl border border-white/16 bg-white/[0.08] px-3 py-3 text-left text-zinc-100 transition hover:border-white/28 hover:bg-white/[0.14] hover:text-white"
                    >
                      <MessageSquare className="h-4 w-4 shrink-0" />
                      <span className="text-sm font-medium">TradoPro Assistant</span>
                    </button>
                    {extraRailActions.map((action) => {
                      const Icon = action.icon || RefreshCw;
                      return (
                        <button
                          key={action.id}
                          type="button"
                          onClick={() => {
                            action.onClick?.();
                            setMobileMenuOpen(false);
                          }}
                          className="glass-unfilled-control flex items-center gap-3 rounded-2xl border border-white/16 bg-white/[0.08] px-3 py-3 text-left text-zinc-100 transition hover:border-white/28 hover:bg-white/[0.14] hover:text-white"
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="text-sm font-medium">{action.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

              <div className="space-y-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-300">
                  Appearance
                </div>
                <div className="grid gap-2">
                  {THEME_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setTheme(option.id, token)}
                      className={cn(
                        "glass-unfilled-control flex items-center gap-3 rounded-2xl border px-3 py-3 text-left transition",
                        theme === option.id
                          ? "border-[var(--ui-accent-border)] bg-[var(--ui-accent-soft)] text-white"
                          : "border-white/16 bg-white/[0.08] text-zinc-100 hover:border-white/28 hover:bg-white/[0.14] hover:text-white",
                      )}
                    >
                      <span
                        className="h-8 w-8 shrink-0 rounded-xl border border-white/10"
                        style={{ background: option.swatch }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium">{option.label}</div>
                        <div className="truncate text-xs text-zinc-300">{option.description}</div>
                      </div>
                      {theme === option.id ? <Check className="h-4 w-4" /> : null}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-300">
                  Background
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {BACKGROUND_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setBackgroundTone(option.id, token)}
                      className={cn(
                        "glass-unfilled-control flex h-11 items-center justify-center rounded-2xl border transition",
                        backgroundTone === option.id
                          ? "border-white/40 bg-white/10"
                          : "border-white/16 bg-white/[0.08] hover:border-white/28 hover:bg-white/[0.14]",
                      )}
                      title={option.label}
                    >
                      <span
                        className="h-5 w-5 rounded-full border border-white/10"
                        style={{
                          background: option.swatch,
                          boxShadow: backgroundTone === option.id ? "0 0 0 4px rgba(255,255,255,0.08)" : "none",
                        }}
                      />
                    </button>
                  ))}
                </div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-300">
                  Accent Color
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {ACCENT_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setAccentColor(option.id)}
                      className={cn(
                        "glass-unfilled-control flex h-11 items-center justify-center rounded-2xl border transition",
                        accentColor === option.id
                          ? "border-white/40 bg-white/10"
                          : "border-white/16 bg-white/[0.08] hover:border-white/28 hover:bg-white/[0.14]",
                      )}
                      title={option.label}
                    >
                      <span
                        className="h-5 w-5 rounded-full"
                        style={{
                          background: option.color,
                          boxShadow: accentColor === option.id ? `0 0 0 4px ${option.soft}` : "none",
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-xs text-zinc-400">
                {showUserEmail && user?.email ? `Signed in as ${user.email}` : "Signed in to TradoPro"}
              </div>

              <Button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setLogoutConfirmOpen(true);
                }}
                className="w-full rounded-2xl bg-red-500/90 text-white hover:bg-red-500"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </SheetContent>
        </Sheet>

        <AlertDialog open={logoutConfirmOpen} onOpenChange={setLogoutConfirmOpen}>
          <AlertDialogContent className="border-white/10 bg-[#080808] text-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Sign Out?</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Do you want to log out of TradoPro now?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/5 hover:text-white">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => logout()}
                className="bg-red-500 text-white hover:bg-red-400"
              >
                Sign Out
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}
